/**
 * <CallSessionProvider>
 *
 * Owns the active Zoom Video SDK client and the "is the doctor currently
 * in an encounter?" state for the duration of the user session. Hoisted in
 * AppRoutes so navigating between chart tabs (Demographics, Labs, etc.)
 * keeps the underlying call alive — that's why the provider lives above
 * <Shell> rather than inside CallPage.
 *
 * Per LD-9, this provider does NOT write Encounter.status — it only tracks
 * local React state for the panel UI. It DOES stamp `Encounter.period.start`
 * on join and `Encounter.period.end` on leave (CEO billing-time prompt) so
 * downstream surfaces — generate-draft AI prompt + Stats Strip Session
 * Duration card + Encounter.length derivation — read real call wall-clock
 * time. The stamping is best-effort: if the encounterId is a stand-in
 * (no real Encounter yet, e.g. the chart-side flow that uses patient id as
 * a placeholder) the call is silently skipped so the Zoom join never
 * blocks on a 404.
 *
 * Lifecycle (post-Phase 3 migration)
 *   1. {@link joinSession} lazy-imports `@zoom/videosdk`, creates the client,
 *      fetches a Zoom JWT via Medplum's `GET /oauth2/call/signature`, then
 *      `client.init` + `client.join` + `mediaStream.startAudio/Video`, and
 *      best-effort stamps `Encounter.period.start` on the FHIR Encounter.
 *   2. The local self-view is rendered via `mediaStream.attachVideo` into the
 *      PiP container registered through {@link attachSelfVideoContainer};
 *      remote participants render into the separate container registered
 *      through {@link attachRemoteVideoContainer} (both registered by
 *      `<PatientVideoFrame>`), driven by the `peer-video-state-change`
 *      listener — which deliberately bails on the local user.
 *   3. {@link leaveSession} detaches the `peer-video-state-change` listener,
 *      tears down the Zoom client, best-effort stamps `Encounter.period.end`,
 *      and resets state.
 *
 * The state-flip-only bridges ({@link markSessionActive}/{@link markSessionEnded})
 * stay available for callers that already own their own Zoom client — they
 * are still useful as pure JOIN/RESET dispatch helpers.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { VideoClient, VideoPlayer } from '@zoom/videosdk'
import { useMedplum } from '@medplum/react'
import { getStatus, isOperationOutcome, OperationOutcomeError } from '@medplum/core'
import type { Encounter } from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'
import { SNOMED_USER_ROLES } from '@/utils'
import { RECORD_DURATION_SEGMENT_PREFIX } from '@/model/middleware-types'
import { isDurationEncounterMissing } from '@/pages/Call/hooks/scribeErrors'
import { isManagerRole } from '@/utils/isManagerRole'
import { useActiveScribeSession } from '@/pages/Call/hooks/useActiveScribeSession'
import { confirmDiscardScribeSession } from '@/pages/Call/hooks/confirmDiscardScribeSession'
import { isJoinClaimConflict } from '@/pages/Call/hooks/scribeErrors'
import { afterSettled } from './joinClaimInFlight'
import { useClient } from '@/hooks/useClient'
import {
  CallSessionContext,
  type CallChatMessage,
  type CallSessionContextValue,
  type JoinSessionParams,
  type JoinSessionResult,
} from './callSessionContext'
import { callSessionReducer, initialCallSessionState } from './callSessionReducer'
import { watchCameraAcquisition, releaseCameraCapture } from './cameraCapture'
import {
  readActiveEncounterMarker,
  writeActiveEncounterMarker,
  clearActiveEncounterMarker,
} from './scribeActiveEncounterStorage'

/**
 * True when `err` is an HTTP 404 in any shape the Medplum SDK / its fetch
 * wrapper throws — `readResource` throws an `OperationOutcomeError` whose
 * `.outcome` holds the FHIR `OperationOutcome`, so a bare `isOperationOutcome`
 * check misses it. Used where a 404 is an expected, non-error outcome.
 */
function isNotFoundError(err: unknown): boolean {
  const outcomeFromError = err instanceof OperationOutcomeError ? err.outcome : undefined
  if (outcomeFromError && isOperationOutcome(outcomeFromError)) {
    try {
      if (getStatus(outcomeFromError) === 404) return true
    } catch {
      // fall through to status sniffing below
    }
  }
  if (isOperationOutcome(err)) {
    try {
      if (getStatus(err) === 404) return true
    } catch {
      // fall through to status sniffing below
    }
  }
  const status =
    (err as { response?: { status?: number }; status?: number } | undefined)?.response?.status ??
    (err as { status?: number } | undefined)?.status
  return status === 404
}

const DOCTOR_VIRTUAL_BACKGROUND_URL = '/images/provider-manager-zoom-bg.png'

/**
 * Whether the branded virtual background is applied automatically when a
 * clinical-credential user joins a call. `true` — doctors and care
 * coordinators (MEDICAL_DOCTOR / HOSPITAL_MANAGER) always START a call with
 * the background on; the §4 "Background" PanelBtn lets them turn it off for
 * the current call only. Kept as a single named switch on purpose so changing
 * the default is a one-line edit.
 */
const VIRTUAL_BACKGROUND_DEFAULT_ON = true

// PD-1439 — this tab's join-claim identity. Module scope, not a ref: a provider
// re-mount must keep the same id or the tab locks itself out.
const CALL_TAB_ID = crypto.randomUUID()

/** Re-assert the claim well inside its server-side TTL (≤30s). */
const JOIN_CLAIM_HEARTBEAT_MS = 10_000
/**
 * After a failed first claim, retry this soon rather than leave the call unrecorded until the first
 * beat. The patient's rejoin check reads the claim, so an unrecorded call looks ended to them.
 */
const JOIN_CLAIM_RETRY_MS = [1_000, 2_000, 4_000]

type Action = 'Start' | 'Stop'
type PeerVideoStateHandler = (payload: { userId: number; action: Action }) => void

/** Participant fields we read from `getAllUser()` / `getUser()` — only what we touch. */
type ZoomParticipant = {
  userId: number
  // Zoom v2.4+ renamed `userIdentity` (JWT `user_identity`) → `userKey`
  // (`user_key`); read `userKey ?? userIdentity` everywhere for forward-compat.
  userKey?: string
  userIdentity?: string
  displayName?: string
  isHost?: boolean
  isManager?: boolean
  isInFailover?: boolean
  bVideoOn?: boolean
}
type ParticipantEventHandler = (payload: Array<{ userId: number }> | { userId: number }) => void
type ConnectionChangeHandler = (payload: { state?: string; reason?: string }) => void

/**
 * Narrow shape of `mediaStream.attachVideo` / `detachVideo` we touch. Matches
 * the runtime contract the Zoom SDK exposes; mirrors the legacy CallPage
 * defensive typing so the provider runs against any minor SDK update without
 * tripping the compiler.
 */
type ZoomMediaStreamLike = {
  startAudio?: (options?: { backgroundNoiseSuppression?: boolean }) => unknown
  startVideo?: () => unknown
  stopVideo?: () => unknown
  muteAudio?: () => unknown
  unmuteAudio?: () => unknown
  mirrorVideo?: (mirrored: boolean) => unknown
  // `undefined` removes the background (turns VB off); a URL or `'blur'` turns
  // it on. Matches the Zoom SDK signature (media.d.ts `updateVirtualBackgroundImage`).
  updateVirtualBackgroundImage?: (imageUrl: string | undefined, cropped?: boolean) => unknown
  attachVideo?: (
    userId: number,
    quality: number,
    element?: string | HTMLElement,
  ) => Promise<unknown>
  detachVideo?: (userId: number) => Promise<VideoPlayer | VideoPlayer[]>
}

type ZoomEventHandler =
  | PeerVideoStateHandler
  | ChatMessageHandler
  | ParticipantEventHandler
  | ConnectionChangeHandler

type ZoomClientInstance = {
  init: (
    lang: string,
    region: string,
    opts: {
      patchJsMedia: boolean
      enforceVirtualBackground?: boolean
      leaveOnPageUnload?: boolean
    },
  ) => Promise<unknown>
  join: (sessionName: string, signature: string, userName: string) => Promise<unknown>
  leave: () => Promise<unknown>
  on: (event: string, handler: ZoomEventHandler) => void
  off: (event: string, handler: ZoomEventHandler) => void
  getMediaStream: () => ZoomMediaStreamLike
  getCurrentUserInfo: () => { userId: number; muted?: boolean; bVideoOn?: boolean }
  getUser?: (userId: number) => ZoomParticipant | undefined
  getAllUser?: () => ZoomParticipant[]
  removeUser?: (userId: number) => Promise<unknown>
  isHost?: () => boolean
  isManager?: () => boolean
  getChatClient?: () => ZoomChatClientLike
}

/**
 * Narrow shape of the Zoom in-call chat client we touch. Mirrors the
 * runtime contract for the SDK's `client.getChatClient()` — only the
 * surface we need for the Chat drawer (send to all + per-message
 * subscription via the `chat-on-message` event below).
 */
export type ZoomChatMessage = {
  id?: string
  message?: string
  timestamp?: number
  sender?: { userId?: number; name?: string }
  receiver?: { userId?: number; name?: string }
}

type ZoomChatClientLike = {
  send?: (text: string, userId: number) => Promise<ZoomChatMessage | Error>
  sendToAll?: (text: string) => Promise<ZoomChatMessage | Error>
}

type ChatMessageHandler = (payload: ZoomChatMessage) => void

/**
 * Zoom `VideoQuality.Video_360P`. Inlined as a literal so the provider stays
 * import-light at module load time — we lazy-import the SDK in `joinSession`.
 * Matches the legacy CallPage value (CallPage.tsx:361).
 */
const VIDEO_QUALITY_360P = 2

/**
 * TEMP diagnostic — dumps the Zoom participant roster so the duplicate-tile fix
 * can be confirmed in dev (watch for two rows sharing one `identity` = a ghost).
 * Tagged `[call-roster]` for easy removal after verification.
 */
function logCallRoster(client: ZoomClientInstance | null, label: string): void {
  if (!client) return
  try {
    const self = client.getCurrentUserInfo()
    const users = client.getAllUser?.() ?? []
    console.log(
      `[call-roster] ${label} | self=${self?.userId} | ${users.length} user(s):`,
      users.map((u) => ({
        userId: u.userId,
        identity: u.userIdentity,
        name: u.displayName,
        host: u.isHost,
        mgr: u.isManager,
        failover: u.isInFailover,
        video: u.bVideoOn,
      })),
    )
  } catch {
    // diagnostic only — never break the call
  }
}

// Resolve a participant's JWT identity (the tile dedup + role-classification
// key) from the roster. Reads `userKey` (Zoom v2.4+) then legacy `userIdentity`.
function resolveIdentity(client: ZoomClientInstance, userId: number): string | undefined {
  const participant =
    client.getUser?.(userId) ?? client.getAllUser?.().find((u) => u.userId === userId)
  return participant?.userKey ?? participant?.userIdentity
}

// A 'guest-…' identity is the patient side (the middleware's patient/guest
// signature route, GET /v2/visits/{encounterId}/call-signature, mints
// `guest-${sessionName}`; the Medplum route signs only for a logged-in
// Practitioner); any other identity is staff (a care coordinator).
// Unknown/blank ⇒ patient, preserving the prior two-party routing + attribution.
function classifyIdentity(identity: string | undefined): 'patient' | 'staff' {
  return !identity || identity.startsWith('guest-') ? 'patient' : 'staff'
}

type RosterPresence = { patientPresent: boolean; staffPresent: boolean; staffDisplayName?: string }

// Roster-derived (idempotent): split the non-self participants into patient
// (guest identity) vs staff presence. Own `getAllUser()` scan — NOT the
// video-gated join loop — so a participant waiting with camera off still
// counts. Excludes self and any ghost sharing self's identity, so it survives
// Zoom's double-emits without flapping.
function scanRosterPresence(client: ZoomClientInstance): RosterPresence {
  try {
    const me = client.getCurrentUserInfo()
    const myIdentity = resolveIdentity(client, me.userId)
    let patientPresent = false
    let staffPresent = false
    let staffDisplayName: string | undefined
    for (const u of client.getAllUser?.() ?? []) {
      if (u.userId === me.userId) continue
      const identity = u.userKey ?? u.userIdentity
      if (myIdentity && identity === myIdentity) continue // reconnect ghost of self
      if (classifyIdentity(identity) === 'staff') {
        staffPresent = true
        staffDisplayName ??= u.displayName
      } else {
        patientPresent = true
      }
    }
    return { patientPresent, staffPresent, staffDisplayName }
  } catch {
    return { patientPresent: false, staffPresent: false }
  }
}

export function CallSessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(callSessionReducer, initialCallSessionState)
  const medplum = useMedplum()
  // `orpcClient`, not `client` — inside every join/leave block `client` is the
  // Zoom video client.
  const orpcClient = useClient()
  const { getActiveSessionStrict, discardSession } = useActiveScribeSession()

  const zoomClientRef = useRef<typeof VideoClient | null>(null)
  const joiningRef = useRef(false)
  const leavingRef = useRef(false)
  const joinedRef = useRef(false)
  /** PD-1439 — join-claim keep-alive; live only between a committed join and its leave. */
  const stopJoinClaimRef = useRef<(() => void) | null>(null)
  // Every beat and retry still on the wire. Release waits for all of them: one served after the
  // release would re-create the claim and keep a finished visit open to the patient for its 30s TTL.
  const joinClaimInFlightRef = useRef<Promise<void>>(Promise.resolve())
  // Bumped by every join's first claim. A release deferred behind an in-flight beat is skipped if a
  // newer join on the same encounter has started: this tab's id is the same, so the release would
  // delete the new call's claim.
  const joinGenerationRef = useRef({ encounterId: null as string | null, generation: 0 })
  // Real Zoom-join instant (ms) — period.start is the booked slot start for
  // calendar visits, so length is derived from this at leave, not from period.
  const joinedAtRef = useRef<number | null>(null)
  // PD-1671 — one id per join, so a drop-and-rejoin reports two segments that
  // accrue while a retry of either one stays idempotent. Not CALL_TAB_ID: that
  // survives a rejoin in the same tab and would swallow the second sitting.
  const joinSegmentIdRef = useRef<string | null>(null)
  /**
   * Mirrors `state.currentEncounterId` so {@link leaveSession} can read the
   * active encounter id without resubscribing to reducer state. The ref is
   * set imperatively next to each dispatch (same pattern as joinedRef).
   */
  const currentEncounterIdRef = useRef<string | null>(null)
  const peerVideoStateHandlerRef = useRef<PeerVideoStateHandler | null>(null)
  const userRemovedHandlerRef = useRef<ParticipantEventHandler | null>(null)
  const userAddedHandlerRef = useRef<ParticipantEventHandler | null>(null)
  const connectionChangeHandlerRef = useRef<ConnectionChangeHandler | null>(null)
  const userUpdatedHandlerRef = useRef<ParticipantEventHandler | null>(null)
  // identity → live tile: one tile per person, newest userId wins (collapses reconnect ghosts).
  const identitySlotsRef = useRef<Map<string, { userId: number; node: HTMLElement }>>(new Map())
  // userId → identity, remembered past a participant's roster departure so a
  // late-arriving caption (Zoom LTT lags 1-3s) still classifies to the right
  // role. Populated on every roster scan; cleared only on join/teardown.
  const identityByUserIdRef = useRef<Map<number, string>>(new Map())
  // userId → its live <video-player> node, so a re-attach (identity resolved
  // late and the tile moved containers) drops the prior placement instead of
  // leaving a duplicate.
  const videoNodeByUserIdRef = useRef<Map<number, HTMLElement>>(new Map())
  // Guards the host-side ghost-eviction sweep against reconnect storms.
  const evictionInFlightRef = useRef(false)
  const remoteContainerRef = useRef<HTMLElement | null>(null)
  // Host for the STAFF (care coordinator) video — the small tile in
  // <PatientVideoFrame>, registered via {@link attachStaffVideoContainer}.
  const staffContainerRef = useRef<HTMLElement | null>(null)
  /**
   * Host for the LOCAL user's self-view video — the PiP tile in
   * `<PatientVideoFrame>`, registered via {@link attachSelfVideoContainer}.
   * Kept separate from {@link remoteContainerRef} because `renderVideo`
   * deliberately bails on the local user (the doctor's video must never paint
   * over the patient frame).
   */
  const selfContainerRef = useRef<HTMLElement | null>(null)
  /**
   * Tracks `{ userId: 'Start' | 'Stop' }` so duplicate `peer-video-state-change`
   * events (the Zoom SDK emits them eagerly on join/leave) don't cause us to
   * re-attach the same `<video-player>` twice. Mirrors the legacy CallPage
   * dedupe at CallPage.tsx:283.
   */
  const sessionStateRef = useRef<Record<number, Action>>({})
  /**
   * Caches the per-session decision "does the local user qualify for the
   * branded virtual background?" so we don't refetch the PractitionerRole on
   * every camera toggle. `null` = not yet resolved, `true`/`false` = resolved.
   * Mirrors the legacy CallPage `shouldApplyBgRef` (CallPage.tsx:286-303).
   */
  const shouldApplyBgRef = useRef<boolean | null>(null)
  // Cached physician decision (MEDICAL_DOCTOR strictly), resolved with shouldApplyBgRef. null = unresolved.
  const isPhysicianRef = useRef<boolean | null>(null)
  // Cached manager decision (HOSPITAL_MANAGER / CC_MANAGER), resolved with isPhysicianRef. null = unresolved.
  const isManagerRef = useRef<boolean | null>(null)
  /**
   * Mirrors {@link isVirtualBackgroundOn} so the imperative join / camera-
   * toggle paths read the current on/off choice without a stale closure (same
   * ref-beside-state pattern as joinedRef). Seeded from the module default.
   */
  const isVbOnRef = useRef(VIRTUAL_BACKGROUND_DEFAULT_ON)
  // [zoom-vb-diag] TEMP — latest VB-model preload readiness, logged at toggle time.
  const vbPreloadReadyRef = useRef<unknown>(undefined)

  const [isAudioMuted, setIsAudioMuted] = useState(true)
  const [isVideoMuted, setIsVideoMuted] = useState(true)
  /**
   * On/off state of the branded virtual background, surfaced to the §4
   * "Background" PanelBtn. Defaults to {@link VIRTUAL_BACKGROUND_DEFAULT_ON}.
   */
  const [isVirtualBackgroundOn, setIsVirtualBackgroundOn] = useState(VIRTUAL_BACKGROUND_DEFAULT_ON)
  /**
   * True once the local user's role resolves to a VB-eligible clinical
   * credential (MEDICAL_DOCTOR / HOSPITAL_MANAGER) — gates whether the
   * "Background" button renders at all. Tracked as state (not only via
   * shouldApplyBgRef) because button visibility needs a re-render when the
   * role resolves on join.
   */
  const [canUseVirtualBackground, setCanUseVirtualBackground] = useState(false)
  // Tri-state: null = role unresolved, false = non-physician, true = physician.
  const [isLocalUserPhysician, setIsLocalUserPhysician] = useState<boolean | null>(null)
  // Tri-state: null = role unresolved, false = not a manager, true = manager.
  const [isLocalUserManager, setIsLocalUserManager] = useState<boolean | null>(null)
  // In-call chat — messages render in `<ChatDrawer>`. Hydrated from
  // Zoom's `chat-on-message` event in `joinSession`; reset on leave.
  const [chatMessages, setChatMessages] = useState<CallChatMessage[]>([])
  // Ref mirrors `chatMessages` so the chat event handler (created once
  // in `joinSession` and captured into the Zoom client's `on(...)` slot)
  // can dedupe by message id without re-subscribing on every render.
  const chatMessagesRef = useRef<CallChatMessage[]>([])
  useEffect(() => {
    chatMessagesRef.current = chatMessages
  }, [chatMessages])
  const chatMessageHandlerRef = useRef<ChatMessageHandler | null>(null)
  // "Ask BeAssistant" rail thread — lives here (not in ZoomPanel) because the
  // rail unmounts on route changes while the call survives; reset with
  // chatMessages on join/leave.
  const [assistantThreadPath, setAssistantThreadPath] = useState<string[] | null>(null)

  /**
   * Render (or tear down) a participant's video into the registered DOM
   * container. The container is the §2 `<PatientVideoFrame>` mount point; the
   * legacy hidden `videoContainerRef` in CallPage is no longer used.
   */
  const renderVideo = useCallback(
    async (event: { action: Action; userId: number }): Promise<void> => {
      const client = zoomClientRef.current as unknown as ZoomClientInstance | null
      if (!client) {
        return
      }
      // Never paint the LOCAL user's video into a remote tile. Zoom can emit
      // `peer-video-state-change` for the local user; the self-view PiP
      // (rendered separately) is the only place the doctor's own video belongs.
      try {
        const selfInfo = client.getCurrentUserInfo()
        if (selfInfo && selfInfo.userId === event.userId) {
          return
        }
      } catch {
        // `getCurrentUserInfo` may throw if the SDK isn't ready yet — fall
        // through and let the existing pipeline handle it.
      }
      let mediaStream: ZoomMediaStreamLike
      try {
        mediaStream = client.getMediaStream()
      } catch (err) {
        logError('call-session', 'renderVideo getMediaStream', err)
        return
      }

      try {
        if (event.action === 'Stop') {
          const element = await mediaStream.detachVideo?.(event.userId)
          if (Array.isArray(element)) {
            element.forEach((el) => (el as unknown as HTMLElement).remove())
          } else if (element) {
            ;(element as unknown as HTMLElement).remove()
          }
          for (const [identity, slot] of identitySlotsRef.current) {
            if (slot.userId === event.userId) identitySlotsRef.current.delete(identity)
          }
          videoNodeByUserIdRef.current.delete(event.userId)
          return
        }

        // Route by identity class: a guest identity → the patient hero tile;
        // staff (care coordinator) → the small staff tile. Bail if that tile
        // isn't mounted yet — its attach*VideoContainer replay renders this
        // user once it is.
        const identity = resolveIdentity(client, event.userId)
        const container =
          classifyIdentity(identity) === 'staff'
            ? staffContainerRef.current
            : remoteContainerRef.current
        if (!container) {
          return
        }

        const userVideo = (await mediaStream.attachVideo?.(event.userId, VIDEO_QUALITY_360P)) as
          | VideoPlayer
          | undefined
        if (!userVideo) {
          return
        }
        const node = userVideo as unknown as HTMLElement
        node.style.width = '100%'
        node.style.height = '100%'
        // Drop this user's prior tile before re-placing — covers a stream that
        // first landed in the wrong container while its identity was still
        // unresolved, so it moves instead of duplicating.
        const priorNode = videoNodeByUserIdRef.current.get(event.userId)
        if (priorNode && priorNode !== node) priorNode.remove()
        // One tile per identity, newest userId wins (collapses reconnect
        // ghosts); unknown identity falls back to plain append.
        if (identity) {
          const prev = identitySlotsRef.current.get(identity)
          if (prev && prev.userId !== event.userId) {
            try {
              await mediaStream.detachVideo?.(prev.userId)
            } catch {
              // best-effort detach of the stale tile
            }
            prev.node.remove()
          }
          identitySlotsRef.current.set(identity, { userId: event.userId, node })
        }
        container.appendChild(node)
        videoNodeByUserIdRef.current.set(event.userId, node)
      } catch (err) {
        logError('call-session', `renderVideo ${event.action}`, err)
      }
    },
    [],
  )

  /**
   * Render (or tear down) the LOCAL user's video into the self-view PiP
   * container. Mirrors {@link renderVideo}'s `attachVideo` → appendChild
   * pipeline but targets {@link selfContainerRef} — the remote pipeline
   * deliberately bails on the local user, so this is the only place the
   * doctor's own track renders.
   */
  const renderSelfVideo = useCallback(async (action: Action): Promise<void> => {
    const container = selfContainerRef.current
    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    if (!client || !container) {
      return
    }
    let selfUserId: number
    try {
      selfUserId = client.getCurrentUserInfo().userId
    } catch (err) {
      logError('call-session', 'renderSelfVideo getCurrentUserInfo', err)
      return
    }
    let mediaStream: ZoomMediaStreamLike
    try {
      mediaStream = client.getMediaStream()
    } catch (err) {
      logError('call-session', 'renderSelfVideo getMediaStream', err)
      return
    }
    try {
      if (action === 'Stop') {
        const element = await mediaStream.detachVideo?.(selfUserId)
        if (Array.isArray(element)) {
          element.forEach((el) => (el as unknown as HTMLElement).remove())
        } else if (element) {
          ;(element as unknown as HTMLElement).remove()
        }
        return
      }

      const selfVideo = (await mediaStream.attachVideo?.(selfUserId, VIDEO_QUALITY_360P)) as
        | VideoPlayer
        | undefined
      if (!selfVideo) {
        return
      }
      const node = selfVideo as unknown as HTMLElement
      node.style.width = '100%'
      node.style.height = '100%'
      // Replace, don't stack: both the join path and a container
      // re-registration can fire Start for the same tile; only the newest
      // <video-player> should live in the PiP.
      container.replaceChildren(node)
    } catch (err) {
      logError('call-session', `renderSelfVideo ${action}`, err)
    }
  }, [])

  /**
   * Self-view companion to {@link attachRemoteVideoContainer}. The PiP tile
   * unmounts its container for the cam-off fallback, so a fresh registration
   * mid-call (camera off → on, or a tree reorder) re-attaches the doctor's
   * video immediately — but only when the SDK reports the camera as on.
   */
  const attachSelfVideoContainer = useCallback(
    (el: HTMLElement | null) => {
      selfContainerRef.current = el
      if (!el) return
      const client = zoomClientRef.current as unknown as ZoomClientInstance | null
      if (!client || !joinedRef.current) return
      try {
        if (client.getCurrentUserInfo()?.bVideoOn) {
          void renderSelfVideo('Start')
        }
      } catch {
        // SDK not ready yet — the join path's own renderSelfVideo covers it.
      }
    },
    [renderSelfVideo],
  )

  // Drop the tracked tiles for one identity class — once their hosting
  // container unmounts the <video-player> nodes are stale; a fresh container
  // replays them. Scoped by class so the hero and staff tiles release
  // independently.
  const releaseTileClass = useCallback((kind: 'patient' | 'staff') => {
    for (const [identity, slot] of identitySlotsRef.current) {
      if (classifyIdentity(identity) === kind) {
        identitySlotsRef.current.delete(identity)
        videoNodeByUserIdRef.current.delete(slot.userId)
      }
    }
  }, [])

  // Replay the Start entries for one identity class into a freshly-registered
  // container. The class filter keeps the hero and staff containers from both
  // claiming the same user (renderVideo also routes by class as a backstop).
  const replayTileClass = useCallback(
    (client: ZoomClientInstance, kind: 'patient' | 'staff') => {
      Object.entries(sessionStateRef.current).forEach(([userId, action]) => {
        if (action !== 'Start') return
        if (classifyIdentity(resolveIdentity(client, Number(userId))) !== kind) return
        void renderVideo({ action, userId: Number(userId) })
      })
    },
    [renderVideo],
  )

  const attachRemoteVideoContainer = useCallback(
    (el: HTMLElement | null) => {
      remoteContainerRef.current = el
      // A fresh container mid-call (e.g. a tab switch reorders the React tree
      // without leaving the call) re-renders every active PATIENT participant.
      if (!el) {
        releaseTileClass('patient')
        return
      }
      const client = zoomClientRef.current as unknown as ZoomClientInstance | null
      if (!client || !joinedRef.current) return
      replayTileClass(client, 'patient')
    },
    [releaseTileClass, replayTileClass],
  )

  // Staff (care coordinator) companion to {@link attachRemoteVideoContainer}.
  // The small tile mounts only while a coordinator is connected, so a fresh
  // registration replays their video; unmount releases the staff-class tiles.
  const attachStaffVideoContainer = useCallback(
    (el: HTMLElement | null) => {
      staffContainerRef.current = el
      if (!el) {
        releaseTileClass('staff')
        return
      }
      const client = zoomClientRef.current as unknown as ZoomClientInstance | null
      if (!client || !joinedRef.current) return
      replayTileClass(client, 'staff')
    },
    [releaseTileClass, replayTileClass],
  )

  // Record the roster's userId→identity pairs (kept past departure for late
  // captions) and dispatch the derived patient/staff presence. Called from
  // every roster-changing Zoom event.
  const refreshRosterPresence = useCallback((client: ZoomClientInstance) => {
    try {
      for (const u of client.getAllUser?.() ?? []) {
        const identity = u.userKey ?? u.userIdentity
        if (identity) identityByUserIdRef.current.set(u.userId, identity)
      }
    } catch {
      // best-effort — the presence dispatch below still runs
    }
    const { patientPresent, staffPresent, staffDisplayName } = scanRosterPresence(client)
    dispatch({ type: 'SET_ROSTER_PRESENCE', patientPresent, staffPresent, staffDisplayName })
  }, [])

  // Classify a live (or just-departed) participant by identity — drives
  // per-role caption attribution in useLiveTranscription. Falls back to the
  // remembered identity map when the user has already left the roster.
  const classifyCallParticipant = useCallback((userId: number): 'self' | 'patient' | 'staff' => {
    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    if (!client) return 'patient'
    try {
      if (client.getCurrentUserInfo().userId === userId) return 'self'
    } catch {
      // fall through — treat as a remote participant
    }
    const identity = resolveIdentity(client, userId) ?? identityByUserIdRef.current.get(userId)
    return classifyIdentity(identity)
  }, [])

  const peerVideoStateHandler = useCallback<PeerVideoStateHandler>(
    (payload) => {
      // Dedupe the SDK's early double-emit by per-user action: forward only
      // when this user's action changed. `renderVideo` itself bails on the
      // local user, so the doctor's own track never lands in the remote container.
      const previous = sessionStateRef.current[payload.userId]
      if (previous !== payload.action) {
        void renderVideo(payload)
      }
      sessionStateRef.current = { ...sessionStateRef.current, [payload.userId]: payload.action }
    },
    [renderVideo],
  )

  // Host/manager-only, best-effort: remove a ghost sharing our own identity. Render dedup
  // already hides the tile, so a removeUser rejection (e.g. manager can't remove host) is fine.
  const evictGhostSelf = useCallback(async (client: ZoomClientInstance): Promise<void> => {
    if (evictionInFlightRef.current) return
    evictionInFlightRef.current = true
    try {
      if (!(client.isHost?.() || client.isManager?.())) return
      const me = client.getCurrentUserInfo()
      const myIdentity = me ? resolveIdentity(client, me.userId) : undefined
      if (!myIdentity || !me) return
      const ghosts = (client.getAllUser?.() ?? []).filter(
        (u) => (u.userKey ?? u.userIdentity) === myIdentity && u.userId !== me.userId,
      )
      for (const ghost of ghosts) {
        console.log(
          `[call-evict] removing ghost userId=${ghost.userId} identity=${ghost.userIdentity}`,
        )
        try {
          await client.removeUser?.(ghost.userId)
        } catch (err) {
          logError('call-session', 'evictGhostSelf removeUser', err)
        }
      }
    } finally {
      evictionInFlightRef.current = false
    }
  }, [])

  // Detach participant-lifecycle listeners, drop their refs + the identity map. Shared by unwind + leave.
  const detachParticipantListeners = useCallback((client: ZoomClientInstance | null) => {
    const offSafe = (event: string, handler: ZoomEventHandler | null) => {
      if (!client || !handler) return
      try {
        client.off(event, handler)
      } catch {
        // SDK may not have registered the listener yet — nothing to detach.
      }
    }
    offSafe('user-removed', userRemovedHandlerRef.current)
    offSafe('user-added', userAddedHandlerRef.current)
    offSafe('connection-change', connectionChangeHandlerRef.current)
    offSafe('user-updated', userUpdatedHandlerRef.current)
    userRemovedHandlerRef.current = null
    userAddedHandlerRef.current = null
    connectionChangeHandlerRef.current = null
    userUpdatedHandlerRef.current = null
    identitySlotsRef.current.clear()
    identityByUserIdRef.current.clear()
    videoNodeByUserIdRef.current.clear()
  }, [])

  /**
   * Best-effort stamp of the Encounter's wall-clock edges. `period.start` is
   * written here on join; the leave edge is POSTed to the middleware instead
   * (see below). The chart-side flow passes a patient id stand-in when no real
   * Encounter exists yet (see EncounterWorkspace comment at lines 242-247), so
   * we MUST treat a 404 as a normal skip — the Zoom join/leave continues
   * uninterrupted.
   *
   * PD-1671 — this component no longer writes `Encounter.length`. It used to,
   * only-if-absent, while the scribe `/finalize` path accumulated into the same
   * field: a visit with both recorded double, and a provider who dropped and
   * rejoined recorded only the first sitting. The middleware endpoint is now
   * the single writer; we report the interval we observed and it derives the
   * length. Semantics: `docs/ENCOUNTER_DURATION.md`.
   *
   * `period.end` moved with it, into that same server-side write. That removes
   * the period.end race; the two callers merging segments into one Encounter is
   * handled server-side with an If-Match precondition and a re-read-and-merge
   * retry, not here.
   */
  const stampEncounterPeriod = useCallback(
    async (encounterId: string, edge: 'start' | 'end'): Promise<void> => {
      if (edge === 'end') {
        const joinedAt = joinedAtRef.current
        const segmentId = joinSegmentIdRef.current
        // A leave with no Zoom join (the interrupted-visit Finish flow seats
        // the encounter without joining) has no interval to report, but
        // `period.end` still has to close: without it the AI's duration ladder
        // falls through to the booked slot start measured to now.
        const measured = joinedAt != null && segmentId != null
        try {
          // Posted on EVERY leave, not only the first: a rejoin is a second
          // segment that has to accrue. Replaying one segment id is a no-op
          // server-side, so a retry can never inflate the total.
          const now = new Date().toISOString()
          await orpcClient.scribeRecordDuration({
            encounterId,
            ...(measured ? { segmentId, startedAt: new Date(joinedAt).toISOString() } : undefined),
            endedAt: now,
            // This tab's clock, so the middleware can move the window onto its
            // own — the one the scribe caller's segment is on.
            clientNow: now,
            stampPeriodEnd: true,
          })
        } catch (err) {
          // Only the handler's "no such Encounter" 404 is the stand-in path. A
          // bare 404 can also mean the endpoint is missing, which must be loud.
          if (isDurationEncounterMissing(err)) return
          logError('call-session', 'stampEncounterPeriod end', err)
        }
        return
      }
      try {
        const existing = await medplum.readResource('Encounter', encounterId)
        // Only stamp start if not already set — protects against re-joins
        // and prevents the first-join wall-clock from being overwritten.
        if (existing.period?.start) {
          return
        }
        await medplum.updateResource<Encounter>({
          ...existing,
          period: { ...(existing.period || {}), start: new Date().toISOString() },
        })
      } catch (err) {
        // 404 = the documented stand-in path (patient id used as encounterId
        // on the chart-side new-encounter flow, see EncounterWorkspace.tsx:
        // 242-247) — expected, not an error; must NOT pollute Sentry. The Zoom
        // lifecycle continues uninterrupted either way.
        if (isNotFoundError(err)) return
        logError('call-session', `stampEncounterPeriod ${edge}`, err)
      }
    },
    [medplum, orpcClient],
  )

  /**
   * Resolve whether the local user qualifies for the branded virtual
   * background — clinical credentials only (MEDICAL_DOCTOR / HOSPITAL_MANAGER);
   * patients and care-coordinator profiles never see the "Background" control.
   * Surfaces the result to {@link canUseVirtualBackground} (button visibility)
   * and caches it in {@link shouldApplyBgRef} so the PractitionerRole is
   * fetched at most once per session. Returns the eligibility so callers can
   * chain the apply step.
   */
  const resolveVbEligibility = useCallback(async (): Promise<boolean> => {
    if (shouldApplyBgRef.current === null) {
      // Self-contained try/catch: a PractitionerRole fetch failure must resolve
      // to "not eligible" rather than surface as an unhandled rejection.
      try {
        const profile = medplum.getProfile()
        if (!profile?.id || profile.resourceType !== 'Practitioner') {
          shouldApplyBgRef.current = false
          isPhysicianRef.current = false
          isManagerRef.current = false
        } else {
          const roles = await medplum.searchResources(
            'PractitionerRole',
            `practitioner=Practitioner/${profile.id}`,
          )
          const roleCode = roles[0]?.code?.[0]?.coding?.[0]?.code
          // Scribe runs only on the treating physician's client: MEDICAL_DOCTOR strictly, NOT HOSPITAL_MANAGER.
          isPhysicianRef.current = roleCode === SNOMED_USER_ROLES.MEDICAL_DOCTOR.code
          // Care coordinator (HOSPITAL_MANAGER) + call-center manager (CC_MANAGER)
          // are barred from transcript/scribe/PN surfaces. Inspect ALL role rows +
          // ALL codings + require the system, mirroring the middleware source of
          // truth (see @/utils/isManagerRole) — a single arbitrary role is not
          // authoritative when a practitioner holds multiple PractitionerRole rows.
          isManagerRef.current = isManagerRole(roles)
          shouldApplyBgRef.current =
            roleCode === SNOMED_USER_ROLES.MEDICAL_DOCTOR.code ||
            roleCode === SNOMED_USER_ROLES.HOSPITAL_MANAGER.code
        }
      } catch (err) {
        logError('call-session', 'resolveVbEligibility', err)
        shouldApplyBgRef.current = false
        isPhysicianRef.current = false
        isManagerRef.current = false
      }
      setCanUseVirtualBackground(shouldApplyBgRef.current)
    }
    // Re-publish from the cached ref each call — the role fetch is gated behind
    // shouldApplyBgRef, so later calls (VB toggle / re-join) still re-seat the state.
    setIsLocalUserPhysician(isPhysicianRef.current)
    setIsLocalUserManager(isManagerRef.current)
    return shouldApplyBgRef.current
  }, [medplum])

  /**
   * (Re-)apply the branded BeHuman virtual background to the local video
   * stream — only when the user qualifies AND the background is currently
   * toggled on ({@link isVbOnRef}). The default is on, so a fresh join
   * applies the background right after eligibility resolves; after a camera
   * toggle it re-applies only when VB is still on. Best-effort: any failure (no
   * PractitionerRole, SDK rejects the URL, browser not cross-origin-isolated,
   * etc.) is logged and swallowed — the call must never be blocked by
   * background art.
   */
  const applyDoctorVirtualBackground = useCallback(
    async (mediaStream: ZoomMediaStreamLike): Promise<void> => {
      try {
        const eligible = await resolveVbEligibility()
        if (eligible && isVbOnRef.current) {
          await mediaStream.updateVirtualBackgroundImage?.(DOCTOR_VIRTUAL_BACKGROUND_URL)
        }
      } catch (err) {
        // [zoom-vb-diag] TEMP — remove after dev capture.
        console.log('%c[zoom-vb-diag] apply FAILED', 'color:#ff1493;font-weight:bold', {
          type: (err as { type?: string })?.type,
          reason: (err as { reason?: string })?.reason,
          errorCode: (err as { errorCode?: number })?.errorCode,
          t: Date.now(),
        })
        logError('call-session', 'applyDoctorVirtualBackground', err)
      }
    },
    [resolveVbEligibility],
  )

  /**
   * PD-1439 — keep the join claim alive while this tab holds the call.
   * Fire-and-forget: the claim guards against a second tab, never the call.
   * `claimed` false means the first claim failed: retry it on JOIN_CLAIM_RETRY_MS, then the beat
   * carries on. A failed first claim's `takeover` is re-sent, by the retries and then by the beats,
   * until one lands: on a Resume after a reload the dead tab's claim is still held for up to 30s,
   * and a plain claim would conflict with it and stop all recovery. Once one lands, beats never take
   * over. A beat re-creates a claim that expired during an outage (the server's claim script takes
   * a free key). A conflict means another tab holds the visit, so every beat and retry stops.
   */
  const startJoinClaimHeartbeat = useCallback(
    (encounterId: string, claimed: boolean, takeover: boolean): void => {
      stopJoinClaimRef.current?.()
      let stopped = false
      let pendingTakeover = !claimed && takeover
      let retryTimer: ReturnType<typeof setTimeout> | undefined
      const stop = () => {
        stopped = true
        clearInterval(beatTimer)
        clearTimeout(retryTimer)
      }
      // Resolves true once nothing more is worth sending: the claim landed or another tab has it.
      const claim = (what: string): Promise<boolean> => {
        const request = orpcClient.scribeJoinClaim({
          encounterId,
          tabId: CALL_TAB_ID,
          takeover: pendingTakeover || undefined,
        })
        joinClaimInFlightRef.current = afterSettled(joinClaimInFlightRef.current, request)
        return request.then(
          () => {
            pendingTakeover = false
            return true
          },
          (err: unknown) => {
            if (isJoinClaimConflict(err)) {
              stop()
              return true
            }
            logError('call-session', `join claim ${what} failed`, err)
            return false
          },
        )
      }
      const retry = (attempt: number) => {
        retryTimer = setTimeout(() => {
          void claim('retry').then((done) => {
            if (!done && !stopped && attempt + 1 < JOIN_CLAIM_RETRY_MS.length) retry(attempt + 1)
          })
        }, JOIN_CLAIM_RETRY_MS[attempt])
      }
      // Declared after `stop`, which only runs once a claim settles, by when this is assigned.
      const beatTimer = setInterval(() => void claim('heartbeat'), JOIN_CLAIM_HEARTBEAT_MS)
      if (!claimed) retry(0)
      stopJoinClaimRef.current = stop
    },
    [orpcClient],
  )

  /** Stop beating and hand the encounter back so the next tab isn't held off for the TTL. */
  const releaseJoinClaim = useCallback(
    (encounterId: string | null): void => {
      stopJoinClaimRef.current?.()
      stopJoinClaimRef.current = null
      if (!encounterId) return
      const { generation } = joinGenerationRef.current
      void joinClaimInFlightRef.current
        .then(() => {
          const latest = joinGenerationRef.current
          if (latest.generation !== generation && latest.encounterId === encounterId) return
          return orpcClient.scribeJoinRelease({ encounterId, tabId: CALL_TAB_ID })
        })
        .catch(() => undefined)
    },
    [orpcClient],
  )

  const joinSession = useCallback(
    async (params: JoinSessionParams): Promise<JoinSessionResult> => {
      // Must wrap getUserMedia before the SDK's first acquisition.
      watchCameraAcquisition()
      const { encounterId, sessionName, userName, resumeSessionId } = params
      if (!encounterId) {
        logError(
          'call-session',
          'joinSession called without encounterId',
          new Error('Missing encounterId'),
        )
        return 'failed'
      }
      if (!sessionName) {
        logError(
          'call-session',
          'joinSession called without sessionName',
          new Error('Missing sessionName'),
        )
        return 'failed'
      }

      // Re-entrancy no-op: a join is already in flight. This is NOT a dead
      // room — return 'busy' so the Resume flow can ignore/retry rather than
      // degrade to Finish / Discard.
      if (joiningRef.current) {
        return 'busy'
      }
      joiningRef.current = true

      try {
        // PD-1439 — claim before anything Zoom-side exists (a refusal leaves
        // nothing to unwind). `takeover` on resume is load-bearing: recovery must
        // never wait out a dead tab's ≤30s claim residue.
        let claimed = true
        joinGenerationRef.current = {
          encounterId,
          generation: joinGenerationRef.current.generation + 1,
        }
        try {
          await orpcClient.scribeJoinClaim({
            encounterId,
            tabId: CALL_TAB_ID,
            takeover: Boolean(resumeSessionId),
          })
        } catch (claimErr) {
          if (isJoinClaimConflict(claimErr)) {
            return 'already-joined'
          }
          // Fail open on anything else — the claim guards against a second tab,
          // it must never be what stops a doctor from reaching a patient.
          logError('call-session', 'join claim failed — proceeding with join', claimErr)
          claimed = false
        }

        // Lazy-import the Zoom Video SDK so it only enters the bundle once a
        // call actually starts. The default export is a factory; we cache the
        // client across joins so reconnect flows can reuse it.
        if (!zoomClientRef.current) {
          const ZoomVideo = (await import('@zoom/videosdk')).default
          zoomClientRef.current = ZoomVideo.createClient()
        }

        // Reset peer-video-state cache for the new session — without this a
        // re-join would see the previous user's "Start" cached and short-
        // circuit `renderVideo`.
        sessionStateRef.current = {}
        identitySlotsRef.current.clear()
        identityByUserIdRef.current.clear()
        videoNodeByUserIdRef.current.clear()

        const client = zoomClientRef.current as unknown as ZoomClientInstance

        const { signature } = await medplum.get(
          `/oauth2/call/signature?sessionName=${encodeURIComponent(sessionName)}`,
        )

        // enforceVirtualBackground: required for VB on the no-SAB WASM engine; inert on WebRTC.
        // leaveOnPageUnload: refresh/close leaves cleanly so no ghost participant lingers.
        await client.init('en-US', 'Global', {
          patchJsMedia: true,
          enforceVirtualBackground: true,
          leaveOnPageUnload: true,
        })

        // Wire the peer listener BEFORE join() so we don't miss the
        // self-join event that the SDK fires immediately after the join
        // promise resolves.
        peerVideoStateHandlerRef.current = peerVideoStateHandler
        client.on('peer-video-state-change', peerVideoStateHandlerRef.current)

        // Wire the chat listener BEFORE join() too so any chat sent in
        // the join handshake window doesn't slip through. The handler
        // dedupes by message id (Zoom can re-emit on transient
        // reconnects) and pushes into the React state surfaced to
        // `<ChatDrawer>` via `useCallSession().chatMessages`.
        setChatMessages([])
        setAssistantThreadPath(null)
        chatMessageHandlerRef.current = (payload: ZoomChatMessage) => {
          if (!payload || typeof payload.message !== 'string') return
          const selfUserId = (() => {
            try {
              return client.getCurrentUserInfo()?.userId
            } catch {
              return undefined
            }
          })()
          const senderUserId = payload.sender?.userId
          // Own messages render from sendChatMessage's local echo — skipping
          // them here kills the duplicate-bubble case where the event id and
          // the echo id disagree (ChatMessage.id is optional in the SDK).
          if (senderUserId !== undefined && senderUserId === selfUserId) return
          const msgId = payload.id ?? `${senderUserId ?? 'anon'}-${payload.timestamp ?? Date.now()}`
          if (chatMessagesRef.current.some((m) => m.id === msgId)) return
          const next: CallChatMessage = {
            id: msgId,
            senderUserId,
            senderName: payload.sender?.name ?? 'Participant',
            message: payload.message,
            timestamp: payload.timestamp ?? Date.now(),
            isOwn: false,
          }
          setChatMessages((prev) => [...prev, next])
        }
        client.on('chat-on-message', chatMessageHandlerRef.current)

        // Duplicate-tile prevention: user-removed detaches the tile (a kicked ghost may
        // never emit a 'Stop'); connection-change/user-added trigger the host-side ghost sweep.
        userRemovedHandlerRef.current = (payload) => {
          const removed = Array.isArray(payload) ? payload : [payload]
          removed.forEach((u) => {
            if (typeof u?.userId !== 'number') return
            void renderVideo({ action: 'Stop', userId: u.userId })
            delete sessionStateRef.current[u.userId]
          })
          logCallRoster(client, 'user-removed')
          refreshRosterPresence(client)
        }
        client.on('user-removed', userRemovedHandlerRef.current)

        userAddedHandlerRef.current = () => {
          logCallRoster(client, 'user-added')
          void evictGhostSelf(client)
          refreshRosterPresence(client)
        }
        client.on('user-added', userAddedHandlerRef.current)

        connectionChangeHandlerRef.current = (payload) => {
          logCallRoster(client, `connection-change:${payload?.state ?? '?'}`)
          // connection-change tracks the LOCAL (provider) link. Only recompute
          // presence once the provider is stably Connected — a provider
          // Reconnecting/Closed blip empties the roster and would wrongly flip
          // a still-present patient to 'disconnected'. Patient join/leave comes
          // through user-added/user-removed instead.
          if (payload?.state === 'Connected') {
            void evictGhostSelf(client)
            refreshRosterPresence(client)
          }
        }
        client.on('connection-change', connectionChangeHandlerRef.current)

        // user-updated fires when a participant's roster fields settle after
        // join — notably `userKey`/`userIdentity`, which can arrive a beat late.
        // Re-deriving presence here reclassifies a staff participant who first
        // appeared identity-less (mounting the coordinator tile, whose
        // registration then replays their video into it).
        userUpdatedHandlerRef.current = () => {
          refreshRosterPresence(client)
        }
        client.on('user-updated', userUpdatedHandlerRef.current)

        // [zoom-vb-diag] TEMP — remove after dev capture. Logs when (and if) the
        // VB model finishes preloading, so we can compare it to the toggle attempt.
        client.on('video-virtual-background-preload-change', (p: unknown) => {
          vbPreloadReadyRef.current = (p as { isReady?: unknown })?.isReady
          console.log('%c[zoom-vb-diag] vb-preload-change', 'color:#ff00ff;font-weight:bold', {
            payload: p,
            t: Date.now(),
          })
        })

        await client.join(sessionName, signature, userName)
        joinedRef.current = true
        joinedAtRef.current = Date.now()
        joinSegmentIdRef.current = `${RECORD_DURATION_SEGMENT_PREFIX}${crypto.randomUUID()}`
        currentEncounterIdRef.current = encounterId
        // The claim expires on its own TTL — hold it for the length of the call.
        startJoinClaimHeartbeat(encounterId, claimed, Boolean(resumeSessionId))

        // `resumeSessionId` adopts the recovered scribe session (interrupted-
        // visit recovery) instead of letting the reducer hard-reset to a
        // fresh null id. Undefined on a normal join.
        dispatch({ type: 'JOIN', encounterId, resumeSessionId })

        // Stamp period.start on the FHIR Encounter once the Zoom join has
        // committed — but never from a manager's client, so a coordinator
        // joining/leaving can't set the visit's wall-clock. Resolve the role
        // first (memoized) so the gate is deterministic, not a mount race.
        // Fire-and-forget: an Encounter write hiccup never strands the join.
        void (async () => {
          await resolveVbEligibility()
          if (isManagerRef.current === true) return
          await stampEncounterPeriod(encounterId, 'start')
        })()

        const mediaStream = client.getMediaStream()

        // [zoom-vb-diag] TEMP — remove after dev capture. Confirms the video path
        // (cross-origin-isolated/SAB vs WASM) and whether the SDK reports VB as
        // supported on dev — the gate between the startVideo fix and an asset/SAB fix.
        console.log('%c[zoom-vb-diag] env', 'color:#ff00ff;font-weight:bold', {
          crossOriginIsolated: globalThis.crossOriginIsolated,
          hasSharedArrayBuffer: typeof SharedArrayBuffer === 'function',
          isSupportVirtualBackground: (
            mediaStream as { isSupportVirtualBackground?: () => boolean }
          ).isSupportVirtualBackground?.(),
          t: Date.now(),
        })

        await mediaStream.startAudio?.({ backgroundNoiseSuppression: true })
        await mediaStream.startVideo?.()

        const info = client.getCurrentUserInfo()
        setIsAudioMuted(info.muted ?? true)
        setIsVideoMuted(!info.bVideoOn)

        // Self-view: the remote pipeline bails on the local user, so the
        // doctor's own track renders separately into the PiP container. When
        // the PiP hasn't mounted yet this is a safe no-op — registering the
        // container (attachSelfVideoContainer) re-attaches.
        await renderSelfVideo('Start')

        // Render participants already video-on at join: peer-video-state-change only
        // fires on a transition, so a late joiner gets no Start for existing streams.
        try {
          for (const user of client.getAllUser?.() ?? []) {
            // Skip self, camera-off, and anyone the handler already rendered during the
            // join awaits — a repeat renderVideo for the same userId appends a duplicate tile.
            if (
              user.userId === info.userId ||
              !user.bVideoOn ||
              sessionStateRef.current[user.userId] === 'Start'
            )
              continue
            sessionStateRef.current = { ...sessionStateRef.current, [user.userId]: 'Start' }
            void renderVideo({ action: 'Start', userId: user.userId })
          }
        } catch (err) {
          logError('call-session', 'render existing participants on join', err)
        }
        // Seed presence from a full roster scan — covers a patient (or
        // coordinator) already waiting in the room before the provider joined
        // (no user-added event fires for them on this client).
        refreshRosterPresence(client)
        // Self-view must match what remote users see — mirroring would flip
        // the branded virtual background's logo/text (PD-1271).
        await mediaStream.mirrorVideo?.(false)
        // Auto-apply the branded BeHuman virtual background for clinical
        // credentials (MEDICAL_DOCTOR / HOSPITAL_MANAGER). No-op for everyone
        // else. Matches the legacy CallPage behavior (CallPage.tsx:344-345).
        await applyDoctorVirtualBackground(mediaStream)
        logCallRoster(client, 'joined')
        return 'joined'
      } catch (err) {
        // Unwind: detach the listener, tear down the (possibly half-joined)
        // client, reset state so the UI returns to the not-joined screen.
        if (peerVideoStateHandlerRef.current && zoomClientRef.current) {
          try {
            ;(zoomClientRef.current as unknown as ZoomClientInstance).off(
              'peer-video-state-change',
              peerVideoStateHandlerRef.current,
            )
          } catch {
            // Ignore — the client may not have initialised far enough to
            // register the listener internally.
          }
        }
        if (chatMessageHandlerRef.current && zoomClientRef.current) {
          try {
            ;(zoomClientRef.current as unknown as ZoomClientInstance).off(
              'chat-on-message',
              chatMessageHandlerRef.current,
            )
          } catch {
            // Ignore — same rationale as the peer listener above.
          }
        }
        chatMessageHandlerRef.current = null
        peerVideoStateHandlerRef.current = null
        detachParticipantListeners(zoomClientRef.current as unknown as ZoomClientInstance | null)
        // The failing step may come AFTER join() succeeded — leave, or the
        // half-joined client lingers as a ghost participant in the session.
        if (zoomClientRef.current && joinedRef.current) {
          try {
            await (zoomClientRef.current as unknown as ZoomClientInstance).leave()
          } catch (leaveErr) {
            logError('call-session', 'joinSession unwind (client.leave)', leaveErr)
          }
        }
        joinedRef.current = false
        joinedAtRef.current = null
        joinSegmentIdRef.current = null
        currentEncounterIdRef.current = null
        // A claim taken for a join that then failed must not hold other tabs off
        // for the residual TTL — this tab isn't in the call.
        releaseJoinClaim(encounterId)
        logError('call-session', 'joinSession failed', err)
        zoomClientRef.current = null
        // The discarded client may have acquired the camera before the failing
        // step — release it, or the webcam stays live on the not-joined screen.
        releaseCameraCapture()
        sessionStateRef.current = {}
        dispatch({ type: 'RESET' })
        setIsAudioMuted(true)
        setIsVideoMuted(true)
        return 'failed'
      } finally {
        joiningRef.current = false
      }
    },
    [
      medplum,
      orpcClient,
      peerVideoStateHandler,
      renderVideo,
      renderSelfVideo,
      evictGhostSelf,
      detachParticipantListeners,
      stampEncounterPeriod,
      applyDoctorVirtualBackground,
      refreshRosterPresence,
      resolveVbEligibility,
      startJoinClaimHeartbeat,
      releaseJoinClaim,
    ],
  )

  const leaveSession = useCallback(async (): Promise<void> => {
    if (leavingRef.current) {
      return
    }
    leavingRef.current = true

    // Snapshot the active encounter id BEFORE the dispatch/cleanup wipes
    // currentEncounterIdRef. The stamp itself is awaited so we don't lose
    // the writeback when the provider unmounts during teardown (logout).
    const activeEncounterId = currentEncounterIdRef.current

    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    try {
      if (client && peerVideoStateHandlerRef.current) {
        try {
          client.off('peer-video-state-change', peerVideoStateHandlerRef.current)
        } catch (err) {
          // Listener teardown isn't worth user-facing noise but is worth
          // diagnostic context.
          logError('call-session', 'leaveSession (off peer-video)', err)
        }
        peerVideoStateHandlerRef.current = null
      }
      if (client && chatMessageHandlerRef.current) {
        try {
          client.off('chat-on-message', chatMessageHandlerRef.current)
        } catch (err) {
          logError('call-session', 'leaveSession (off chat-on-message)', err)
        }
        chatMessageHandlerRef.current = null
      }
      detachParticipantListeners(client)
      setChatMessages([])
      setAssistantThreadPath(null)
      if (client && joinedRef.current) {
        try {
          await client.leave()
        } catch (err) {
          // Leaving when not joined throws; that's expected during teardown
          // and not actionable. Lower-level errors are still worth knowing.
          logError('call-session', 'leaveSession (client.leave)', err)
        }
      }
      // Stamp period.end on the FHIR Encounter once Zoom has released the
      // media stream. Awaited so the write completes before the provider's
      // state is reset — this is the canonical signal that drives Session
      // Duration in the Stats Strip and durationMinutes for the AI E/M
      // grounding. Helper is defensive: silent no-op for stand-in ids or
      // when period.end was already set by an earlier Progress Note save.
      // Never stamp period.end/length from a manager's client. isManagerRef is
      // tri-state (null = unresolved) — stamp ONLY when the local user is
      // positively a non-manager, so an unresolved role can't corrupt the
      // physician's Session Duration / E/M grounding. The role normally
      // resolves during join, but a leave that races an in-flight join could
      // still see null, so re-resolve here first (memoized, never throws,
      // fail-open to non-manager) — mirrors the join-time start-stamp gate.
      if (activeEncounterId) {
        await resolveVbEligibility()
        if (isManagerRef.current === false) {
          await stampEncounterPeriod(activeEncounterId, 'end')
        }
      }
    } finally {
      zoomClientRef.current = null
      joinedRef.current = false
      joinedAtRef.current = null
      joinSegmentIdRef.current = null
      currentEncounterIdRef.current = null
      sessionStateRef.current = {}
      dispatch({ type: 'RESET' })
      // Normal teardown (End call / navigate away) — drop the recovery marker
      // so a later reload doesn't offer to resume an ended session.
      clearActiveEncounterMarker()
      // PD-1439 — stop the keep-alive and hand the encounter straight back, so
      // the doctor's next tab isn't refused for the rest of the claim's TTL.
      releaseJoinClaim(activeEncounterId)
      setIsAudioMuted(true)
      setIsVideoMuted(true)
      // A VB toggle lasts one call only (VIRTUAL_BACKGROUND_DEFAULT_ON) —
      // without this reset, a doctor who turned it off would stay
      // background-less on their NEXT call instead of starting back on.
      // Role-derived state (shouldApplyBgRef / canUseVirtualBackground and
      // isPhysicianRef / isLocalUserPhysician) is deliberately kept: the role
      // doesn't change between calls.
      isVbOnRef.current = VIRTUAL_BACKGROUND_DEFAULT_ON
      setIsVirtualBackgroundOn(VIRTUAL_BACKGROUND_DEFAULT_ON)
      leavingRef.current = false
    }
  }, [stampEncounterPeriod, detachParticipantListeners, resolveVbEligibility, releaseJoinClaim])

  const toggleMute = useCallback(async (): Promise<void> => {
    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    if (!client || !joinedRef.current) {
      // No live session — just flip the optimistic state so consumers stay
      // in sync. The provider treats audio as muted by default.
      setIsAudioMuted((prev) => !prev)
      return
    }
    let mediaStream: ZoomMediaStreamLike
    try {
      mediaStream = client.getMediaStream()
    } catch (err) {
      logError('call-session', 'toggleMute getMediaStream', err)
      return
    }
    const currentlyMuted = isAudioMuted
    try {
      if (currentlyMuted) {
        await mediaStream.unmuteAudio?.()
      } else {
        await mediaStream.muteAudio?.()
      }
      // Read back from the SDK so we mirror the authoritative state instead
      // of guessing at the post-toggle value (the SDK may refuse to unmute
      // if the browser has revoked permission mid-call).
      const info = client.getCurrentUserInfo()
      setIsAudioMuted(info.muted ?? !currentlyMuted)
    } catch (err) {
      logError('call-session', 'toggleMute', err)
    }
  }, [isAudioMuted])

  const toggleVideo = useCallback(async (): Promise<void> => {
    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    if (!client || !joinedRef.current) {
      // No live session — local-only optimistic flip. The §2 PatientVideoFrame
      // self-view PiP reads from getUserMedia, so toggling video here without
      // a Zoom session still drives the avatar/PiP fallback correctly.
      setIsVideoMuted((prev) => !prev)
      return
    }
    let mediaStream: ZoomMediaStreamLike
    try {
      mediaStream = client.getMediaStream()
    } catch (err) {
      logError('call-session', 'toggleVideo getMediaStream', err)
      return
    }
    const currentlyMuted = isVideoMuted
    try {
      if (currentlyMuted) {
        await mediaStream.startVideo?.()
        // Re-render the self-view manually: the SDK does NOT re-emit
        // `peer-video-state-change` for the local user.
        await renderSelfVideo('Start')
        // Re-apply the branded BG — the SDK drops it on stopVideo, so a
        // doctor toggling their camera off and back on would otherwise see
        // their real room until next join.
        await applyDoctorVirtualBackground(mediaStream)
      } else {
        await mediaStream.stopVideo?.()
        await renderSelfVideo('Stop')
        // The SDK keeps the camera claimed after stopVideo (webcam LED stays
        // on); it re-acquires on the next startVideo, so releasing is safe.
        releaseCameraCapture()
      }
      const updated = client.getCurrentUserInfo()
      setIsVideoMuted(!updated.bVideoOn)
    } catch (err) {
      logError('call-session', 'toggleVideo', err)
    }
  }, [isVideoMuted, renderSelfVideo, applyDoctorVirtualBackground])

  const toggleVirtualBackground = useCallback(async (): Promise<void> => {
    const next = !isVbOnRef.current
    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    if (!client || !joinedRef.current) {
      // No live Zoom session — flip the optimistic state only. There's no media
      // stream to apply a background to, but consumers stay in sync.
      isVbOnRef.current = next
      setIsVirtualBackgroundOn(next)
      return
    }
    let mediaStream: ZoomMediaStreamLike
    try {
      mediaStream = client.getMediaStream()
    } catch (err) {
      logError('call-session', 'toggleVirtualBackground getMediaStream', err)
      return
    }
    try {
      // [zoom-vb-diag] TEMP — remove after dev capture. The decisive snapshot at
      // the moment VB fails: was the model ever preload-ready, and is the page
      // cross-origin-isolated (SAB)?
      console.log('%c[zoom-vb-diag] toggle attempt', 'color:#ff00ff;font-weight:bold', {
        next,
        crossOriginIsolated: globalThis.crossOriginIsolated,
        hasSharedArrayBuffer: typeof SharedArrayBuffer === 'function',
        vbPreloadReady: vbPreloadReadyRef.current,
        isSupportVirtualBackground: (
          mediaStream as { isSupportVirtualBackground?: () => boolean }
        ).isSupportVirtualBackground?.(),
        t: Date.now(),
      })
      // `undefined` removes the background; the URL applies it. Read back from
      // the SDK is not exposed for VB, so we mirror our own intent on success.
      await mediaStream.updateVirtualBackgroundImage?.(
        next ? DOCTOR_VIRTUAL_BACKGROUND_URL : undefined,
      )
      isVbOnRef.current = next
      setIsVirtualBackgroundOn(next)
      // [zoom-vb-diag] TEMP — remove after dev capture.
      console.log('%c[zoom-vb-diag] toggle OK', 'color:#00ff00;font-weight:bold', {
        next,
        t: Date.now(),
      })
    } catch (err) {
      // [zoom-vb-diag] TEMP — remove after dev capture.
      console.log('%c[zoom-vb-diag] toggle FAILED', 'color:#ff1493;font-weight:bold', {
        type: (err as { type?: string })?.type,
        reason: (err as { reason?: string })?.reason,
        errorCode: (err as { errorCode?: number })?.errorCode,
        t: Date.now(),
      })
      // Swallow + log (e.g. errorCode 6114 VIDEO_VB_MISMATCH_STATE): the call
      // must never break over background art — the button simply won't flip on.
      logError('call-session', 'toggleVirtualBackground', err)
    }
  }, [])

  // Belt-and-suspenders cleanup if the provider itself unmounts mid-call
  // (e.g. on logout). The chart-nav case is already covered because the
  // provider sits above Shell and survives Outlet swaps.
  useEffect(() => {
    return () => {
      const client = zoomClientRef.current as unknown as ZoomClientInstance | null
      if (client) {
        client.leave().catch(() => {
          // unmount path — nothing actionable to do
        })
        zoomClientRef.current = null
      }
    }
  }, [])

  // [zoom-vb-diag] TEMP — remove after dev capture. Surfaces any CSP block (a VB
  // model/wasm/worker fetch the dev CSP rejects that localhost — no CSP — allows),
  // which would explain why the model preload never readies on dev.
  useEffect(() => {
    const onCsp = (e: SecurityPolicyViolationEvent) =>
      console.log('%c[zoom-vb-diag] CSP VIOLATION', 'color:#ff8c00;font-weight:bold', {
        blockedURI: e.blockedURI,
        violatedDirective: e.violatedDirective,
        effectiveDirective: e.effectiveDirective,
        sourceFile: e.sourceFile,
        disposition: e.disposition,
        t: Date.now(),
      })
    document.addEventListener('securitypolicyviolation', onCsp)
    return () => document.removeEventListener('securitypolicyviolation', onCsp)
  }, [])

  // [zoom-vb-diag] TEMP — remove after dev capture. The VB model preloads in a Web
  // Worker whose failure surfaces only as isReady:false (no reason); catch any
  // error/rejection that bubbles to the window in case the cause leaks out here.
  useEffect(() => {
    const onErr = (e: ErrorEvent) =>
      console.log('%c[zoom-vb-diag] window error', 'color:#ff1493;font-weight:bold', {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        t: Date.now(),
      })
    const onRej = (e: PromiseRejectionEvent) =>
      console.log('%c[zoom-vb-diag] unhandledrejection', 'color:#ff1493;font-weight:bold', {
        reason: String(e.reason),
        t: Date.now(),
      })
    window.addEventListener('error', onErr)
    window.addEventListener('unhandledrejection', onRej)
    return () => {
      window.removeEventListener('error', onErr)
      window.removeEventListener('unhandledrejection', onRej)
    }
  }, [])

  // beforeunload guard
  //  - Attach a listener whenever the doctor is mid-call so refresh / close
  //    raises the browser's confirm-leave prompt. Modern browsers ignore the
  //    custom return string and show their own copy — that's expected; the
  //    point is to make sure an accidental Cmd-W doesn't silently tear down
  //    the Zoom client + transcript buffer.
  //  - Detach on unmount AND every time `isJoined` flips to false (i.e. after
  //    leaveSession resolves), so navigating away from a finished call
  //    doesn't keep prompting the user on subsequent reloads.
  useEffect(() => {
    if (!state.isJoined) {
      return
    }
    const beforeUnloadHandler = (event: BeforeUnloadEvent) => {
      // Setting returnValue (legacy) + returning a non-empty string is the
      // documented contract that triggers the confirm dialog. The dialog
      // copy itself is controlled by the browser; our string is unused.
      event.preventDefault()
      const message = 'You are in an active call session. Leaving will end the call.'
      event.returnValue = message
      return message
    }
    window.addEventListener('beforeunload', beforeUnloadHandler)
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler)
    }
  }, [state.isJoined])

  // Interrupted-visit recovery marker (chart surface only — see
  // scribeActiveEncounterStorage). A recoverable session exists exactly when a
  // scribe id is live AND we're streaming (a fresh start or a resume-adopt).
  // We deliberately key on the 'streaming' transition rather than on
  // setScribeSessionId, because the recovery onFinish path seats the scribe id
  // with scribeState='idle' while finalizing — writing a marker there (for a
  // session being ended) would be wrong and would race the finalize clear.
  // Clearing is explicit on teardown (leaveSession), finalize
  // (useScribeSession), and Discard — never here, so a `paused` session keeps
  // its marker.
  useEffect(() => {
    if (!state.scribeSessionId || state.scribeState !== 'streaming') return
    const encounterId = state.currentEncounterId
    if (!encounterId) return
    let cancelled = false
    void (async () => {
      try {
        const enc = await medplum.readResource('Encounter', encounterId)
        if (cancelled) return
        const ref = enc.subject?.reference?.split('/') // e.g. ['Patient', 'abc']
        if (ref?.[0] !== 'Patient' || !ref[1]) return // stand-in / non-patient subject
        // Preserve the ORIGINAL startedAt when a marker for this same encounter
        // already exists. A resume-adopt also sets scribeState='streaming', so
        // this effect re-fires on resume; overwriting startedAt with "now"
        // would make a second interruption's captured-duration label
        // under-report. Keep the earliest start.
        const existing = readActiveEncounterMarker()
        const startedAt =
          existing?.encounterId === encounterId && existing.startedAt
            ? existing.startedAt
            : new Date().toISOString()
        writeActiveEncounterMarker({
          encounterId,
          patientId: ref[1],
          startedAt,
        })
      } catch (err) {
        // 404 = patient-id stand-in (no real Encounter yet), expected; don't
        // log. Other failures are best-effort — recovery degrades to
        // proactive-only.
        if (isNotFoundError(err)) return
        logError('scribe-recovery', 'writeActiveEncounterMarker (read Encounter)', err)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [state.scribeSessionId, state.scribeState, state.currentEncounterId, medplum])

  // Interrupted-visit recovery — on-mount hydration for the CHART surface.
  // Reads the marker (the chart has no encounter id in its URL), re-probes
  // `/active`, and on a live session dispatches HYDRATE_INTERRUPTED so the
  // chart resume card can render. Distinguishes a definitive-empty response
  // (clear the marker) from a failed probe (keep it — a transient blip must
  // not destroy the recovery pointer). Runs once; the `/call` surface recovers
  // independently from its own URL encounter id and does not need this.
  useEffect(() => {
    const marker = readActiveEncounterMarker()
    if (!marker) return
    let cancelled = false
    void (async () => {
      let session
      try {
        session = await getActiveSessionStrict(marker.encounterId)
      } catch (err) {
        // 403 = a manager's client (BlockManagersGuard) — deterministic, this
        // user can never recover the session; drop the marker instead of
        // re-logging on every mount.
        const status =
          (err as { response?: { status?: number }; status?: number } | undefined)?.response
            ?.status ?? (err as { status?: number } | undefined)?.status
        if (status === 403) {
          clearActiveEncounterMarker()
          return
        }
        // Transient failure — keep the marker so a later reload (once the
        // middleware is back) can still recover.
        logError('scribe-recovery', 'hydration probe failed; marker kept', err)
        return
      }
      if (cancelled) return
      if (session && session.status === 'live' && session.chunkCount > 0) {
        dispatch({
          type: 'HYDRATE_INTERRUPTED',
          session: {
            encounterId: session.encounterId,
            patientId: session.patientId ?? marker.patientId,
            sessionId: session.sessionId,
            chunkCount: session.chunkCount,
            startedAt: session.startedAt,
            lastChunkAt: session.lastChunkAt,
          },
        })
      } else {
        // Definitive empty (finalized / discarded / inaccessible) — safe to clear.
        clearActiveEncounterMarker()
      }
    })()
    return () => {
      cancelled = true
    }
    // Mount-only: deliberately empty deps (getActiveSessionStrict is stable).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Pure state-flip bridges for callers that own their own Zoom client.
  // Same JOIN/RESET dispatch as joinSession/leaveSession but without Zoom SDK
  // allocation — the external owner is responsible for the actual video
  // session lifecycle. Useful for tests + future external integrations.
  const markSessionActive = useCallback((encounterId: string) => {
    if (!encounterId) {
      logError(
        'call-session',
        'markSessionActive called without encounterId',
        new Error('Missing encounterId'),
      )
      return
    }
    currentEncounterIdRef.current = encounterId
    dispatch({ type: 'JOIN', encounterId })
  }, [])

  const markSessionEnded = useCallback(() => {
    currentEncounterIdRef.current = null
    dispatch({ type: 'RESET' })
    // The session has ended (today: the interrupted-visit partial-finalize path
    // in EncounterWorkspace, which finalizes WITHOUT joining Zoom and unwinds
    // via this bridge rather than leaveSession). Drop the recovery marker here
    // too — symmetric with leaveSession + useScribeSession.finalize — so a
    // finalized session doesn't leave a stale marker to be cleaned up only on
    // the next load's /active probe.
    clearActiveEncounterMarker()
  }, [])

  const setScribeSessionId = useCallback((sessionId: string | null) => {
    dispatch({ type: 'SET_SCRIBE_SESSION', sessionId })
  }, [])

  const setScribeState = useCallback((next: CallSessionContextValue['scribeState']) => {
    dispatch({ type: 'SET_SCRIBE_STATE', scribeState: next })
  }, [])

  const setEncounterLanguage = useCallback(
    (language: CallSessionContextValue['encounterLanguage']) => {
      dispatch({ type: 'SET_ENCOUNTER_LANGUAGE', language })
    },
    [],
  )

  const discardInterrupted = useCallback(async (): Promise<void> => {
    const session = state.interruptedSession
    if (!session) return
    const lines = session.chunkCount
    return new Promise<void>((resolve) => {
      confirmDiscardScribeSession({
        lineCount: lines,
        onCancel: () => resolve(),
        onConfirm: () => {
          void (async () => {
            try {
              await discardSession(session.encounterId, session.sessionId)
            } catch (err) {
              // Best-effort — clear locally even if the DELETE failed so the
              // doctor isn't trapped with a panel they can't dismiss.
              logError('scribe-recovery', 'discardInterrupted failed', err)
            }
            clearActiveEncounterMarker()
            dispatch({ type: 'CLEAR_INTERRUPTED' })
            resolve()
          })()
        },
      })
    })
  }, [state.interruptedSession, discardSession])

  const sendChatMessage = useCallback(async (text: string): Promise<void> => {
    const trimmed = text.trim()
    if (!trimmed) return
    const client = zoomClientRef.current as unknown as ZoomClientInstance | null
    if (!client) {
      throw new Error('Not in a call — chat is unavailable.')
    }
    const chatClient = client.getChatClient?.()
    if (!chatClient || typeof chatClient.sendToAll !== 'function') {
      throw new Error('Zoom chat client is not available.')
    }
    const result = await chatClient.sendToAll(trimmed)
    if (result instanceof Error) {
      throw result
    }
    // Push the sent message to local state so the doctor sees their own
    // message immediately. The `chat-on-message` event also fires for the
    // sender, but the dedupe-by-id guard in the handler prevents a
    // double-render.
    const info = (() => {
      try {
        return client.getCurrentUserInfo()
      } catch {
        return undefined
      }
    })()
    const msgId = result?.id ?? `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    setChatMessages((prev) => {
      if (prev.some((m) => m.id === msgId)) return prev
      return [
        ...prev,
        {
          id: msgId,
          senderUserId: info?.userId,
          senderName: result?.sender?.name ?? 'You',
          message: trimmed,
          timestamp: result?.timestamp ?? Date.now(),
          isOwn: true,
        },
      ]
    })
  }, [])

  const value = useMemo<CallSessionContextValue>(
    () => ({
      currentEncounterId: state.currentEncounterId,
      isJoined: state.isJoined,
      scribeSessionId: state.scribeSessionId,
      scribeState: state.scribeState,
      encounterLanguage: state.encounterLanguage,
      setEncounterLanguage,
      patientPresence: state.patientPresence,
      staffPresence: state.staffPresence,
      staffDisplayName: state.staffDisplayName,
      classifyCallParticipant,
      isAudioMuted,
      isVideoMuted,
      joinSession,
      leaveSession,
      toggleMute,
      toggleVideo,
      isVirtualBackgroundOn,
      canUseVirtualBackground,
      isLocalUserPhysician,
      isLocalUserManager,
      toggleVirtualBackground,
      attachRemoteVideoContainer,
      attachStaffVideoContainer,
      attachSelfVideoContainer,
      markSessionActive,
      markSessionEnded,
      setScribeSessionId,
      setScribeState,
      interruptedSession: state.interruptedSession,
      discardInterrupted,
      chatMessages,
      sendChatMessage,
      assistantThreadPath,
      setAssistantThreadPath,
      get zoomClient() {
        return zoomClientRef.current
      },
    }),
    [
      state.currentEncounterId,
      state.isJoined,
      state.scribeSessionId,
      state.scribeState,
      state.encounterLanguage,
      setEncounterLanguage,
      state.patientPresence,
      state.staffPresence,
      state.staffDisplayName,
      classifyCallParticipant,
      state.interruptedSession,
      discardInterrupted,
      isAudioMuted,
      isVideoMuted,
      joinSession,
      leaveSession,
      toggleMute,
      toggleVideo,
      isVirtualBackgroundOn,
      canUseVirtualBackground,
      isLocalUserPhysician,
      isLocalUserManager,
      toggleVirtualBackground,
      attachRemoteVideoContainer,
      attachStaffVideoContainer,
      attachSelfVideoContainer,
      markSessionActive,
      markSessionEnded,
      setScribeSessionId,
      setScribeState,
      chatMessages,
      sendChatMessage,
      assistantThreadPath,
    ],
  )

  return <CallSessionContext.Provider value={value}>{children}</CallSessionContext.Provider>
}
